import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GeometryType } from "@evergis/api";
import { useMapContext } from "@evergis/react";
import { Bbox } from "@evergis/sgis";

import { useCreateFeature } from "evergis/core/feature/createFeature";
import { getAppConfig } from "../../../ducks/appConfig";
import { resetSelectedFeatures, setNextFeature, setPrevFeature } from "../../../ducks/selectFeatures/selectFeatures";
import { getCurrentFeatureIndex, getSelectedFeaturesCount } from "../../../ducks/selectFeatures/selectors";
import { clearGeometrySelectionHistory } from "../../../ducks/geometrySelection/geometrySelection";
import { resetFeatureToShow, setPreviousFeatureReference } from "../../../ducks/showFeature/showFeature";
import { useActionWithArgs } from "../../../hooks/useAction";
import { useResetFeatureSelect } from "../../FeatureSelector/hooks/useResetFeatureSelect";
import { useResetFeatureCard } from "./useResetFeatureCard";
import { useGeometrySelectionContext } from "../../../hooks/geometrySelection/useGeometrySelectionContext";

import { FeatureDcExtended } from "../../../ducks/selectFeatures/types";

const DEFAULT_ZOOM_LEVEL = 17;

export const useFeatureCard = () => {
  const dispatch = useDispatch();
  const resetSelected = useResetFeatureSelect();
  const currentIndex = useSelector(getCurrentFeatureIndex);
  const itemsCount = useSelector(getSelectedFeaturesCount);
  const { map: currentMap, painter } = useMapContext();
  const { settings } = useSelector(getAppConfig);
  const createFeature = useCreateFeature();
  const { purgeFeatures } = useGeometrySelectionContext();

  const onClose = useCallback(() => {
    purgeFeatures?.();

    dispatch(setPreviousFeatureReference(undefined));
    dispatch(resetFeatureToShow());
    dispatch(resetSelectedFeatures());
    dispatch(clearGeometrySelectionHistory());
  }, [dispatch, purgeFeatures]);

  const closeCard = useResetFeatureCard(onClose);
  const [onNext, onPrev] = [useActionWithArgs(setNextFeature), useActionWithArgs(setPrevFeature)];

  const zoomToFeature = useCallback(
    (feature: FeatureDcExtended, resolution?: number) => {
      const sGisFeature = createFeature(feature);

      if (!sGisFeature) {
        return;
      }

      const projectedSGisFeature = sGisFeature.projectTo(currentMap.crs);

      const defaultZoomLevel = currentMap.tileScheme.levels.find(
        ({ zIndex }) => zIndex === (settings?.defaultPointZoomLevel || DEFAULT_ZOOM_LEVEL),
      );
      const newResolution =
        feature.geometry.type === GeometryType.Point && !resolution
          ? defaultZoomLevel?.resolution
          : resolution || currentMap.resolution;

      if (feature.geometry.type === GeometryType.Point) {
        currentMap.animateTo(projectedSGisFeature.centroid, newResolution);
      } else {
        // TODO sGisFeature.projectTo does not project bbox
        const { xMin, yMax, xMax, yMin } = projectedSGisFeature.bbox;

        painter.show(new Bbox([xMin, yMax], [xMax, yMin], currentMap.crs), true);
      }
    },
    [currentMap, painter, settings],
  );

  return [
    {
      currentIndex,
      itemsCount,
    },
    {
      closeCard,
      onNext,
      onPrev,
      zoomToFeature,
      resetSelected,
    },
  ];
};
